import React from "react";
import { useTranslation } from "react-i18next";
import {
  Cloud,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Compass,
  Droplets,
  LocateFixed,
  MapPin,
  RefreshCw,
  Sunrise,
  Sunset,
  ThermometerSun,
  Waves,
  Wind,
} from "lucide-react";
import { trackProductEvent } from "../analytics/trackEvent";
import { useFishingToday } from "../features/fishingToday/useFishingToday";
import MoonPhaseIcon from "./weather/MoonPhaseIcon";
import SpeciesIntelligenceSection from "./fishingToday/SpeciesIntelligenceSection";
import "../styles/components/FishingTodayCard.css";

const isPresent = (value) => value !== null && value !== undefined && value !== "";
const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);

const formatNumber = (value, locale, maximumFractionDigits = 0) =>
  isFiniteNumber(value)
    ? new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value)
    : null;

export const formatTemperature = (value, locale) =>
  isFiniteNumber(value) ? `${formatNumber(value, locale, 1)}°C` : null;

export const formatWind = (value, locale) =>
  isFiniteNumber(value) ? `${formatNumber(value, locale)} km/h` : null;

export const formatLocationLocalTime = (value, locale) => {
  const match = String(value || "").match(
    /(?:^|T|\s)(\d{1,2}):(\d{2})(?:\s*([AP]M))?/i
  );
  if (!match) return value || null;
  let hour = Number(match[1]);
  const meridiem = match[3]?.toUpperCase();
  if (meridiem === "AM" && hour === 12) hour = 0;
  if (meridiem === "PM" && hour !== 12) hour += 12;
  const syntheticUtc = new Date(Date.UTC(2020, 0, 1, hour, Number(match[2])));
  return new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(syntheticUtc);
};

const formatTideEvent = (event, locale) => {
  if (!event) return null;
  const parts = [];
  const time = formatLocationLocalTime(event.time, locale);
  if (time) parts.push(time);
  if (isFiniteNumber(event.heightM)) {
    parts.push(`${formatNumber(event.heightM, locale, 1)} m`);
  }
  return parts.length ? parts.join(" · ") : null;
};

const weatherIconFor = (condition = "") => {
  const normalized = condition.toLowerCase();
  if (/thunder|lightning/.test(normalized)) return CloudLightning;
  if (/snow|sleet|ice|blizzard/.test(normalized)) return CloudSnow;
  if (/rain|drizzle|shower/.test(normalized)) return CloudRain;
  if (/fog|mist|haze/.test(normalized)) return CloudFog;
  if (/cloud|overcast/.test(normalized)) return Cloud;
  return CloudSun;
};

const Metric = ({ icon: Icon, label, value }) => {
  if (!isPresent(value)) return null;
  return (
    <div className="rp-fishing-today__metric">
      <Icon size={17} aria-hidden="true" />
      <span>
        <span className="rp-fishing-today__metric-label">{label}</span>
        <strong>{value}</strong>
      </span>
    </div>
  );
};

const FishingTodaySkeleton = ({ t }) => (
  <section
    className="rp-fishing-today rp-fishing-today--skeleton"
    aria-label={t("fishing_today_loading")}
    aria-busy="true"
  >
    <span className="rp-fishing-today__skeleton rp-fishing-today__skeleton--title" />
    <span className="rp-fishing-today__skeleton rp-fishing-today__skeleton--hero" />
    <div className="rp-fishing-today__skeleton-row">
      <span className="rp-fishing-today__skeleton" />
      <span className="rp-fishing-today__skeleton" />
      <span className="rp-fishing-today__skeleton" />
    </div>
  </section>
);

const LocationUnavailable = ({ geolocationSupported, onOpenProfile, onUseGps, t }) => (
  <section className="rp-fishing-today" aria-labelledby="fishing-today-title">
    <div className="rp-fishing-today__empty">
      <MapPin size={28} aria-hidden="true" />
      <h2 id="fishing-today-title">{t("fishing_today_title")}</h2>
      <p>{t("fishing_today_location_missing_body")}</p>
      <div className="rp-fishing-today__button-row">
        <button type="button" className="rp-fishing-today__button" onClick={onOpenProfile}>
          {t("fishing_today_add_profile_location")}
        </button>
        {geolocationSupported ? (
          <button
            type="button"
            className="rp-fishing-today__button rp-fishing-today__button--secondary"
            onClick={onUseGps}
          >
            <LocateFixed size={16} aria-hidden="true" />
            {t("fishing_today_use_current_location")}
          </button>
        ) : null}
      </div>
    </div>
  </section>
);

function FishingTodayCard({
  favoriteSpotOutlookContext = null,
  onLeaveFavoriteSpot,
  onOpenProfile = () => {},
  onOpenBioEditor = () => {},
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.resolvedLanguage || i18n?.language || "en";
  const {
    data,
    error,
    gpsError,
    isInitialLoading,
    isRefreshing,
    activeLocation,
    geolocationSupported,
    requestPreciseLocation,
    useProfileLocation,
    retry,
  } = useFishingToday({
    favoriteSpotOutlookContext,
    onLeaveFavoriteSpot,
  });

  const openProfile = (reason) => {
    trackProductEvent(
      reason === "water_preference_missing"
        ? "fishing_today_preference_cta_clicked"
        : "fishing_today_profile_location_cta_clicked",
      {
        featureArea: "fishing_today",
        metadata: { reason },
      }
    );
    if (reason === "water_preference_missing") {
      onOpenBioEditor();
      return;
    }
    onOpenProfile();
  };

  if (isInitialLoading && !data) return <FishingTodaySkeleton t={t} />;

  if (!data && error?.code === "location_unavailable") {
    return (
      <LocationUnavailable
        geolocationSupported={geolocationSupported}
        onOpenProfile={() => openProfile("location_missing")}
        onUseGps={requestPreciseLocation}
        t={t}
      />
    );
  }

  if (!data) {
    return (
      <section className="rp-fishing-today" aria-labelledby="fishing-today-title">
        <div className="rp-fishing-today__empty" role="alert">
          <CloudSun size={28} aria-hidden="true" />
          <h2 id="fishing-today-title">{t("fishing_today_title")}</h2>
          <p>{t("fishing_today_unavailable_body")}</p>
          <button type="button" className="rp-fishing-today__button" onClick={retry}>
            <RefreshCw size={16} aria-hidden="true" />
            {t("fishing_today_retry")}
          </button>
        </div>
      </section>
    );
  }

  const weather = data.weather || {};
  const current = weather.current || {};
  const today = weather.today || {};
  const astronomy = weather.astronomy || {};
  const hourly = Array.isArray(weather.hourlyRestOfDay)
    ? weather.hourlyRestOfDay.slice(0, 6)
    : [];
  const hasCurrentConditions =
    isPresent(current.condition) ||
    [
      current.temperatureC,
      current.feelsLikeC,
      current.windKph,
      current.gustKph,
      current.precipitationMm,
    ].some(isFiniteNumber);
  const hasDailySummary = [
    today.minimumTemperatureC,
    today.maximumTemperatureC,
    today.precipitationChancePercent,
  ].some(isFiniteNumber) || [astronomy.sunrise, astronomy.sunset].some(isPresent);
  const hasWeather = hasCurrentConditions || hasDailySummary || hourly.length > 0;
  const marine = data.marine || {};
  const hasMarineSst = isFiniteNumber(marine.seaSurfaceTemperatureC);
  const hasMarineTides = Boolean(
    formatTideEvent(marine.nextHigh, locale) ||
      formatTideEvent(marine.nextLow, locale)
  );
  const hasMarineDetails =
    marine.available && (hasMarineSst || hasMarineTides);
  const moon = data.moon || {};
  const hasMoon =
    typeof moon.phase === "string" &&
    moon.phase.trim() !== "" &&
    moon.phase !== "Unknown";
  const moonIlluminationPercent =
    isFiniteNumber(moon.illumination) && moon.illumination >= 0 && moon.illumination <= 1
      ? Math.round(moon.illumination * 100)
      : null;
  const WeatherIcon = weatherIconFor(current.condition);
  const locationName = [data.location?.city, data.location?.country]
    .filter(Boolean)
    .join(", ");
  const sourceIsLive = activeLocation?.source === "live_gps";
  const sourceIsFavorite = activeLocation?.source === "favorite_spot";
  const activeFavoriteContext =
    sourceIsFavorite &&
    activeLocation?.identityKey === favoriteSpotOutlookContext?.spotKey
      ? favoriteSpotOutlookContext
      : null;
  const showPreferenceCta =
    data.preference?.isKnown === false || marine.reason === "unknown_preference";
  const gpsErrorKey = `fishing_today_gps_${gpsError}`;

  return (
    <section
      className="rp-fishing-today"
      aria-labelledby="fishing-today-title"
      aria-busy={isRefreshing}
    >
      <header className="rp-fishing-today__header">
        <div>
          <span className="rp-fishing-today__eyebrow">{t("fishing_today_eyebrow")}</span>
          <h2 id="fishing-today-title">{t("fishing_today_title")}</h2>
          <p className="rp-fishing-today__location">
            <MapPin size={15} aria-hidden="true" />
            <span>
              {locationName || t("fishing_today_near_you")}
              {" · "}
              {sourceIsLive
                ? t("fishing_today_device_location")
                : sourceIsFavorite
                  ? t("favorite_spots")
                  : t("fishing_today_profile_location")}
            </span>
          </p>
        </div>
        <div className="rp-fishing-today__header-actions">
          <button
            type="button"
            className="rp-fishing-today__icon-button"
            onClick={retry}
            disabled={isRefreshing}
            aria-label={t("fishing_today_refresh")}
            title={t("fishing_today_refresh")}
          >
            <RefreshCw size={18} aria-hidden="true" className={isRefreshing ? "is-spinning" : ""} />
          </button>
        </div>
      </header>

      <div className="rp-fishing-today__location-actions">
        {sourceIsLive || sourceIsFavorite ? (
          <button type="button" className="rp-fishing-today__text-button" onClick={useProfileLocation}>
            {t("fishing_today_return_to_profile")}
          </button>
        ) : null}
        {!sourceIsLive && geolocationSupported ? (
          <button type="button" className="rp-fishing-today__text-button" onClick={requestPreciseLocation}>
            <LocateFixed size={15} aria-hidden="true" />
            {t("fishing_today_improve_with_device")}
          </button>
        ) : null}
        {isRefreshing ? (
          <span className="rp-fishing-today__updating" role="status">
            {t("fishing_today_updating")}
          </span>
        ) : null}
      </div>

      {gpsError ? (
        <div className="rp-fishing-today__notice" role="status">
          <span>{t(gpsErrorKey, { defaultValue: t("fishing_today_gps_failure") })}</span>
          <button type="button" onClick={useProfileLocation}>
            {t("fishing_today_keep_profile_location")}
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="rp-fishing-today__notice" role="status">
          <span>{t("fishing_today_refresh_failed")}</span>
          <button type="button" onClick={retry}>{t("fishing_today_retry")}</button>
        </div>
      ) : null}

      <SpeciesIntelligenceSection
        activeLocation={activeLocation}
        generalConditions={data.generalConditions || null}
        initialFishingSetting={activeFavoriteContext?.fishingSetting || null}
        initialFishingPlatform={activeFavoriteContext?.platform || null}
        outlookLocationKey={
          sourceIsFavorite
            ? activeLocation.identityKey
            : sourceIsLive
              ? "live"
              : locationName
        }
      />

      {hasWeather ? (
        <>
      {hasCurrentConditions ? (
      <div className="rp-fishing-today__current">
        <div className="rp-fishing-today__current-condition">
          <WeatherIcon size={46} strokeWidth={1.6} aria-hidden="true" />
          <div>
            <strong className="rp-fishing-today__temperature">
              {formatTemperature(current.temperatureC, locale) || "—"}
            </strong>
            <span>{current.condition || t("fishing_today_conditions")}</span>
            {weather.localTime ? (
              <small>
                {t("fishing_today_local_time", {
                  time: formatLocationLocalTime(weather.localTime, locale),
                })}
              </small>
            ) : null}
          </div>
        </div>
        <div className="rp-fishing-today__metrics">
          <Metric
            icon={ThermometerSun}
            label={t("fishing_today_feels_like")}
            value={formatTemperature(current.feelsLikeC, locale)}
          />
          <Metric
            icon={Wind}
            label={t("fishing_today_wind")}
            value={
              formatWind(current.windKph, locale) &&
              `${formatWind(current.windKph, locale)}${
                current.windDirection ? ` ${current.windDirection}` : ""
              }`
            }
          />
          <Metric
            icon={Compass}
            label={t("fishing_today_gusts")}
            value={formatWind(current.gustKph, locale)}
          />
          <Metric
            icon={Droplets}
            label={t("fishing_today_rain")}
            value={
              isFiniteNumber(current.precipitationMm)
                ? `${formatNumber(current.precipitationMm, locale, 1)} mm`
                : null
            }
          />
        </div>
      </div>
      ) : null}

      {hasDailySummary ? (
      <div className="rp-fishing-today__daily" aria-label={t("fishing_today_daily_summary")}>
        <Metric
          icon={ThermometerSun}
          label={t("fishing_today_low_high")}
          value={
            isFiniteNumber(today.minimumTemperatureC) ||
            isFiniteNumber(today.maximumTemperatureC)
              ? `${formatTemperature(today.minimumTemperatureC, locale) || "—"} / ${
                  formatTemperature(today.maximumTemperatureC, locale) || "—"
                }`
              : null
          }
        />
        <Metric
          icon={CloudRain}
          label={t("fishing_today_precipitation")}
          value={
            isFiniteNumber(today.precipitationChancePercent)
              ? `${formatNumber(today.precipitationChancePercent, locale)}%`
              : null
          }
        />
        <Metric
          icon={Sunrise}
          label={t("fishing_today_sunrise")}
          value={formatLocationLocalTime(astronomy.sunrise, locale)}
        />
        <Metric
          icon={Sunset}
          label={t("fishing_today_sunset")}
          value={formatLocationLocalTime(astronomy.sunset, locale)}
        />
      </div>
      ) : null}

      {hourly.length ? (
        <div className="rp-fishing-today__forecast-section">
          <h3>{t("fishing_today_rest_of_day")}</h3>
          <div className="rp-fishing-today__hourly" tabIndex="0" aria-label={t("fishing_today_hourly_forecast")}>
            {hourly.map((hour, index) => {
              const HourIcon = weatherIconFor(hour.condition);
              return (
                <div className="rp-fishing-today__hour" key={`${hour.timestamp || "hour"}-${index}`}>
                  <time dateTime={hour.timestamp || undefined}>
                    {formatLocationLocalTime(hour.timestamp, locale) || t("fishing_today_later")}
                  </time>
                  <HourIcon size={23} aria-hidden="true" />
                  <strong>{formatTemperature(hour.temperatureC, locale) || "—"}</strong>
                  {isFiniteNumber(hour.precipitationChancePercent) ? (
                    <span>
                      <Droplets size={12} aria-hidden="true" />
                      {formatNumber(hour.precipitationChancePercent, locale)}%
                    </span>
                  ) : null}
                  {isFiniteNumber(hour.windKph) ? (
                    <span>
                      <Wind size={12} aria-hidden="true" />
                      {formatWind(hour.windKph, locale)}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
        </>
      ) : (
        <div className="rp-fishing-today__weather-unavailable" role="status">
          <CloudSun size={24} aria-hidden="true" />
          <div>
            <strong>{t("fishing_today_weather_unavailable_title")}</strong>
            <span>{t("fishing_today_weather_unavailable_body")}</span>
          </div>
          <button type="button" className="rp-fishing-today__text-button" onClick={retry}>
            {t("fishing_today_retry")}
          </button>
        </div>
      )}

      {marine.eligible && hasMarineDetails ? (
        <div className="rp-fishing-today__marine">
          <div className="rp-fishing-today__marine-heading">
            <Waves size={20} aria-hidden="true" />
            <h3>{t("fishing_today_marine_title")}</h3>
          </div>
          <div className="rp-fishing-today__marine-grid">
            <Metric
              icon={ThermometerSun}
              label={t("fishing_today_sea_temperature")}
              value={formatTemperature(marine.seaSurfaceTemperatureC, locale)}
            />
            <Metric
              icon={Waves}
              label={t("fishing_today_next_high")}
              value={formatTideEvent(marine.nextHigh, locale)}
            />
            <Metric
              icon={Waves}
              label={t("fishing_today_next_low")}
              value={formatTideEvent(marine.nextLow, locale)}
            />
          </div>
        </div>
      ) : marine.eligible && !hasMarineDetails ? (
        <p className="rp-fishing-today__marine-unavailable">
          <Waves size={16} aria-hidden="true" />
          {t("fishing_today_marine_unavailable")}
        </p>
      ) : showPreferenceCta ? (
        <div className="rp-fishing-today__preference">
          <Waves size={18} aria-hidden="true" />
          <span>{t("fishing_today_preference_prompt")}</span>
          <button type="button" onClick={() => openProfile("water_preference_missing")}>
            {t("fishing_today_set_preference")}
          </button>
        </div>
      ) : null}

      {hasMoon ? (
        <section className="rp-fishing-today__moon" aria-labelledby="fishing-today-moon-title">
          <MoonPhaseIcon phase={moon.phase} className="rp-fishing-today__moon-image" />
          <div className="rp-fishing-today__moon-copy">
            <span id="fishing-today-moon-title" className="rp-fishing-today__moon-label">
              {t("moon_phase", { defaultValue: "Moon Phase" })}
            </span>
            <strong>{moon.phase}</strong>
            {moonIlluminationPercent !== null ? (
              <span className="rp-fishing-today__moon-meta">
                {t("Illumination", { defaultValue: "Illumination" })}: {moonIlluminationPercent}%
              </span>
            ) : null}
          </div>
        </section>
      ) : null}
    </section>
  );
}

export default FishingTodayCard;
